<template>
  <app-modal
    ref="modal"
    v-bind="$attrs"
    class="modal-container"
    content-class="modal-medium"
    title="オプション設定変更"
  >
    <template v-slot:body="{ cancel }">
      <cross-validation-observer v-slot="{ handleSubmit }" as="div">
        <v-form novalidate @submit.stop.prevent="handleSubmit(submit)">
          <v-card-text class="pa-0">
            <div class="modal-body">
              <v-container>
                <v-row>
                  <v-col cols="12">
                    <app-label-group
                      label="対象V番号"
                      :value="user.contractInfo.vnumber"
                    />
                  </v-col>
                </v-row>
                <template v-if="form.additionalLogCapacity !== null">
                  <v-row>
                    <v-col>
                      <app-badge
                        item="追加ログ保存容量"
                        :isRequired="form.additionalLog === 'ON'"
                        :isInfo="false"
                      />
                      <div class="ml-3">
                        {{ `ログ保存容量設定 ${currentLogSize}GB` }}
                      </div>
                      <div class="d-flex justify-start align-start ml-2">
                        <v-radio-group
                          inline
                          v-model="form.additionalLog"
                          hide-details
                          class="flex-grow-0"
                        >
                          <v-radio label="OFF" value="OFF" />
                          <v-radio label="ON" value="ON" />
                        </v-radio-group>
                        <template v-if="form.additionalLog === 'ON'">
                          <app-select
                            class="ml-3 flex-grow-0"
                            v-model="form.additionalLogCapacity"
                            :items="logStorageOptions"
                            item-title="text"
                            item-value="value"
                            hide-details
                          ></app-select>
                        </template>
                      </div>
                    </v-col>
                  </v-row>
                </template>
                <template v-if="form.isAllowLog !== null">
                  <v-row>
                    <v-col>
                      <app-badge
                        item="Allowログ"
                        :isRequired="false"
                        :isInfo="false"
                      />
                      <v-radio-group
                        inline
                        v-model="form.isAllowLog"
                        class="ml-3"
                        hide-details
                      >
                        <v-radio label="OFF" :value="false" />
                        <v-radio label="ON" :value="true" />
                      </v-radio-group>
                    </v-col>
                  </v-row>
                </template>
                <template v-if="form.isStatistics !== null">
                  <v-row>
                    <v-col>
                      <app-badge
                        item="統計情報レポート"
                        :isRequired="false"
                        :isInfo="false"
                      />
                      <v-radio-group
                        inline
                        v-model="form.isStatistics"
                        class="ml-3"
                        hide-details
                      >
                        <v-radio label="OFF" :value="false" />
                        <v-radio label="ON" :value="true" />
                      </v-radio-group>
                    </v-col>
                  </v-row>
                </template>
              </v-container>
            </div>
            <cross-validation-provider
              :passed="objectChanged"
              vid="objectChanged"
              class="mb-4"
            >
              {{ $filter.message("no_change_data") }}
            </cross-validation-provider>
          </v-card-text>
          <v-divider></v-divider>
          <!-- FOOTER -->
          <v-card-actions class="modal-footer">
            <v-list-item class="w-100 pa-1">
              <template v-slot:append>
                <v-btn variant="flat" color="primary" type="submit">変更</v-btn>
                <v-btn variant="outlined" color="primary" @click="cancel"
                  >キャンセル</v-btn
                >
              </template>
            </v-list-item>
          </v-card-actions>
        </v-form>
      </cross-validation-observer>
    </template>
  </app-modal>
</template>

<script lang="ts">
import type { PropType } from "vue";
import { defineComponent } from "vue";
import type {
  ContractChangeOption,
  ContractChangeOptionPut,
} from "@/apis/ContractChangeOptionApi";
import ContractOptionConfirm from "@/modals/applianceContractSetting/options/ContractOptionConfirm.vue";
import { VueFinalModal } from "vue-final-modal";

export default defineComponent({
  name: "ContractOptionModify",
  props: {
    contractOption: {
      type: Object as PropType<ContractChangeOption>,
      required: true,
    },
  },
  data() {
    return {
      /** ユーザ設定 */
      user: this.$store.state.user,
      cloneItem: {
        ...this.contractOption,
        additionalLog:
          this.contractOption.additionalLogCapacity === 0 ? "OFF" : "ON",
      },
      form: {
        ...this.contractOption,
        additionalLog:
          this.contractOption.additionalLogCapacity === 0 ? "OFF" : "ON",
      },
      logStorageOptions: [
        { value: 10, text: " 10GB " },
        { value: 20, text: " 20GB " },
        { value: 30, text: " 30GB " },
        { value: 40, text: " 40GB " },
        { value: 50, text: " 50GB " },
        { value: 60, text: " 60GB " },
        { value: 70, text: " 70GB " },
        { value: 80, text: " 80GB " },
        { value: 90, text: " 90GB " },
      ],
    };
  },
  computed: {
    /** 現在ログ保存容量設定 */
    currentLogSize(): number {
      if (
        this.form.additionalLog === "ON" &&
        this.form &&
        this.form.additionalLogCapacity
      ) {
        return 10 + this.form.additionalLogCapacity;
      }
      return 10;
    },
    objectChanged(): boolean {
      if (this.form.additionalLog === "OFF") {
        return this.$crossValidation.objectChanged(
          {
            additionalLog: this.cloneItem.additionalLog,
            isAllowLog: this.cloneItem.isAllowLog,
            isStatistics: this.cloneItem.isStatistics,
          },
          {
            additionalLog: this.form.additionalLog,
            isAllowLog: this.form.isAllowLog,
            isStatistics: this.form.isStatistics,
          }
        );
      } else {
        return this.$crossValidation.objectChanged(this.cloneItem, this.form);
      }
    },
  },
  watch: {
    "form.additionalLog": function (newVal: string, oldVal: string) {
      if (oldVal === "OFF" && newVal === "ON") {
        if (this.form.additionalLogCapacity === 0) {
          this.form.additionalLogCapacity = 10;
        }
      }
    },
  },
  methods: {
    /** 変更ボタン押下時の挙動*/
    async submit() {
      /** オプション契約変更確認モーダルの表示 */
      await this.$modal.show(ContractOptionConfirm, {
        contractOption: this.form,
        additionalLog: this.form.additionalLog,
      });
      const putItem: ContractChangeOptionPut = {
        contractOptionKeyId: this.form.contractOptionKeyId,
        additionalLogCapacity:
          this.form.additionalLog === "OFF"
            ? 0
            : this.form.additionalLogCapacity,
        isAllowLog: this.form.isAllowLog,
        isStatistics: this.form.isStatistics,
        note: null,
      };
      await this.$api.contractChangeOption.putContractOptions(putItem);
      // 契約情報が変更されるためプロビジョニング完了後に再取得してvuexのデータを更新
      await this.$store.dispatch("provisioning/addCompletedEvent", async () => {
        await this.$store.dispatch("user/updateLoginInfo", {
          contractSummary: await this.$api.contract.getSummary(),
        });
      });
      (this.$refs.modal as typeof VueFinalModal & { ok: () => void }).ok();
    },
  },
});
</script>
